import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface GlobalLoadingInterceptorProps {
  defaultPrimaryTimeout?: number;
  defaultSecondaryTimeout?: number;
  tabSwitchPrimaryTimeout?: number;
  tabSwitchSecondaryTimeout?: number;
  checkInterval?: number;
}

const LAST_TYPING_TIMESTAMP_KEY_GLOBAL = 'lastUserTypingTimestamp_Global'; // Use a different key if needed, or share
const TYPING_GRACE_PERIOD_MS_GLOBAL = 3000;

/**
 * Checks if the user is currently typing in an input field, textarea, or contenteditable element.
 * Also updates a timestamp in session storage if typing is detected.
 */
function isUserTypingGlobal(): boolean { // Renamed to avoid conflict if these files are ever merged/refactored
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }
  const tagName = activeElement.tagName.toLowerCase();
  const isContentEditable = activeElement.hasAttribute('contenteditable') && activeElement.getAttribute('contenteditable') !== 'false';
  const typing = tagName === 'input' || tagName === 'textarea' || isContentEditable;
  if (typing) {
    sessionStorage.setItem(LAST_TYPING_TIMESTAMP_KEY_GLOBAL, Date.now().toString());
  }
  return typing;
}

/**
 * Checks if the user was typing within the last `TYPING_GRACE_PERIOD_MS_GLOBAL`.
 */
function wasUserTypingRecentlyGlobal(): boolean {
  if (isUserTypingGlobal()) return true;

  const lastTypingTimeStr = sessionStorage.getItem(LAST_TYPING_TIMESTAMP_KEY_GLOBAL);
  if (!lastTypingTimeStr) {
    return false;
  }
  const lastTypingTime = parseInt(lastTypingTimeStr, 10);
  return (Date.now() - lastTypingTime) < TYPING_GRACE_PERIOD_MS_GLOBAL;
}

/**
 * A component that monitors for stuck loading spinners.
 * If spinners persist beyond primaryTimeout, it first tries to invalidate React Query queries.
 * If spinners still persist after an additional secondaryTimeout, it then forces a page refresh.
 * This process is paused if the user is actively typing.
 */
export function GlobalLoadingInterceptor({
  defaultPrimaryTimeout = 20000, 
  defaultSecondaryTimeout = 15000,
  tabSwitchPrimaryTimeout = 8000, // More aggressive for tab switch scenario
  tabSwitchSecondaryTimeout = 7000, // More aggressive for tab switch scenario
  checkInterval = 3000,
}: GlobalLoadingInterceptorProps) {
  const queryClient = useQueryClient();
  const recoveryAttemptedRef = useRef(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (wasUserTypingRecentlyGlobal()) {
        console.log('GlobalLoadingInterceptor: User was typing recently, pausing spinner check.');
        return;
      }

      const isRecentTabSwitch = sessionStorage.getItem('recentTabSwitch') === 'true';
      const currentPrimaryTimeout = isRecentTabSwitch ? tabSwitchPrimaryTimeout : defaultPrimaryTimeout;
      const currentSecondaryTimeout = isRecentTabSwitch ? tabSwitchSecondaryTimeout : defaultSecondaryTimeout;

      if (isRecentTabSwitch) {
        console.log(`GlobalLoadingInterceptor: Recent tab switch detected. Using aggressive timeouts: P=${currentPrimaryTimeout}ms, S=${currentSecondaryTimeout}ms`);
      }

      const spinners = document.querySelectorAll('.animate-spin, .smart-loading-spinner, .enhanced-loading-spinner');
      const spinnerVisibleSinceStr = sessionStorage.getItem('spinnerVisibleSince');

      if (spinners.length > 0) {
        if (!spinnerVisibleSinceStr) {
          console.log(`GlobalLoadingInterceptor: Detected ${spinners.length} loading spinner(s), starting monitoring.`);
          sessionStorage.setItem('spinnerVisibleSince', Date.now().toString());
          recoveryAttemptedRef.current = false;
        } else {
          const spinnerStartTime = parseInt(spinnerVisibleSinceStr, 10);
          const spinnerDuration = Date.now() - spinnerStartTime;

          if (!recoveryAttemptedRef.current && spinnerDuration > currentPrimaryTimeout) {
            console.warn(`GlobalLoadingInterceptor: Spinners persisted for ${(spinnerDuration / 1000).toFixed(1)}s (timeout: ${currentPrimaryTimeout / 1000}s). Attempting query invalidation.`);
            // TODO: Consider user notification
            queryClient.invalidateQueries();
            recoveryAttemptedRef.current = true;
            // Reset timer for secondary timeout
            sessionStorage.setItem('spinnerVisibleSince', Date.now().toString());
          } else if (recoveryAttemptedRef.current && spinnerDuration > currentSecondaryTimeout) {
            console.error(`GlobalLoadingInterceptor: Spinners still active after query invalidation for ${(spinnerDuration / 1000).toFixed(1)}s (timeout: ${currentSecondaryTimeout / 1000}s). Suggesting recovery.`);
            sessionStorage.removeItem('spinnerVisibleSince'); // Stop further checks by this interceptor
            sessionStorage.setItem('recoverySuggested_GlobalInterceptor', `Spinners stuck after query invalidation for ${currentSecondaryTimeout / 1000}s`);
            // window.location.reload(); // Automatic reload removed
          }
        }
      } else if (spinnerVisibleSinceStr) {
        console.log('GlobalLoadingInterceptor: No spinners detected, clearing monitoring.');
        sessionStorage.removeItem('spinnerVisibleSince');
        recoveryAttemptedRef.current = false;
      }
    }, checkInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [defaultPrimaryTimeout, defaultSecondaryTimeout, tabSwitchPrimaryTimeout, tabSwitchSecondaryTimeout, checkInterval, queryClient]);

  return null;
}
